import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Session, User, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updateProfile: (data: { name?: string, avatar_url?: string }) => Promise<{ error: Error | null }>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to refresh the session
  const refreshSession = async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error("Error refreshing session:", error);
        return;
      }
      
      setSession(data.session);
      setUser(data.session?.user ?? null);
    } catch (error) {
      console.error("Exception during session refresh:", error);
    }
  };

  useEffect(() => {
    // Set up auth state listener first to avoid race conditions
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log("Auth state changed:", event, newSession?.user?.email);
        
        if (newSession !== session) {
          setSession(newSession);
          setUser(newSession?.user ?? null);
        }
        
        setIsLoading(false);
        
        // Handle different auth events with more detailed user feedback
        if (event === 'SIGNED_IN') {
          console.log('')
        } else if (event === 'SIGNED_OUT') {
          toast.info("You have been signed out");
        } else if (event === 'USER_UPDATED') {
          toast.success("Your profile has been updated");
        } else if (event === 'PASSWORD_RECOVERY') {
          toast.info("Password recovery initiated");
        } else if (event === 'TOKEN_REFRESHED') {
          console.log("Auth token refreshed successfully");
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (currentSession) {
        console.log("Retrieved existing session for:", currentSession.user.email);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
      }
      setIsLoading(false);
    });

    // Set up session refresh interval - refresh token every hour if user is logged in
    const refreshInterval = setInterval(() => {
      if (session) {
        console.log("Attempting scheduled token refresh");
        refreshSession();
      }
    }, 60 * 60 * 1000); // 1 hour

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  const signOut = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error signing out:", error);
        toast.error(`Error signing out: ${error.message}`);
      }
    } catch (error) {
      console.error("Exception during sign out:", error);
      toast.error("An unexpected error occurred during sign out");
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        let errorMessage = "Sign in failed";
        
        // Provide more specific error messages based on error type
        if (error.message.includes("Invalid login")) {
          errorMessage = "Invalid email or password";
        } else if (error.message.includes("Email not confirmed")) {
          errorMessage = "Please confirm your email before logging in";
        }
        
        toast.error(errorMessage);
      }
      return { error };
    } catch (e) {
      const error = e as AuthError;
      console.error("Sign in exception:", error);
      toast.error(`Something went wrong during sign in`);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            email // Store email in metadata for profile creation
          }
        }
      });
      if (error) {
        let errorMessage = `Sign up failed: ${error.message}`;
        
        // Provide more specific error messages based on error type
        if (error.message.includes("already registered")) {
          errorMessage = "This email is already registered";
        } else if (error.message.includes("password")) {
          errorMessage = "Password doesn't meet requirements";
        }
        
        toast.error(errorMessage);
      } else {
        toast.success('Registration successful! Please check your email for confirmation.');
      }
      return { error };
    } catch (e) {
      const error = e as AuthError;
      console.error("Sign up exception:", error);
      toast.error(`Something went wrong during sign up`);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        toast.error(`Password reset failed: ${error.message}`);
      } else {
        toast.success('Password reset email sent. Please check your inbox.');
      }
      return { error };
    } catch (e) {
      const error = e as AuthError;
      console.error("Password reset exception:", error);
      toast.error(`Something went wrong during password reset`);
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (data: { name?: string, avatar_url?: string }) => {
    try {
      setIsLoading(true);
      if (!user) {
        throw new Error("No user logged in");
      }

      const updates = {
        ...data,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      toast.success('Profile updated successfully!');
      return { error: null };
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error('Failed to update profile');
      return { error: error as Error };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    session,
    isAuthenticated: !!user,
    isLoading,
    signOut,
    signIn,
    signUp,
    resetPassword,
    updateProfile,
    refreshSession
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export both names for the provider to fix the import in App.tsx
export { AuthContext };
export const AuthContextProvider = AuthProvider;
